import React, { useState, useEffect, useRef } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { apiClient } from '../services/base';

interface AddressResult {
  id: string;
  address: string;
  latitude: number;
  longitude: number;
}

interface AzureAddressAutocompleteProps {
  value: string;
  onChange: (address: string, lat: string, lon: string) => void;
  label?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  placeholder?: string;
}

const AzureAddressAutocomplete: React.FC<AzureAddressAutocompleteProps> = ({
  value,
  onChange,
  label = 'Address',
  disabled = false,
  error = false,
  helperText,
  placeholder,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [options, setOptions] = useState<AddressResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const searchAddresses = async (query: string) => {
    if (!query || query.length < 3) {
      setOptions([]);
      return;
    }

    setLoading(true);
    try {
      const resp = await apiClient.get('/maps/search', {
        params: { q: query, limit: 8 },
      });
      const results: AddressResult[] = (resp.data?.results || []).filter(
        (r: any) => r.address && r.latitude != null && r.longitude != null
      );
      setOptions(results);
      setUnavailable(false);
    } catch (err: any) {
      setOptions([]);
      // 503 means the server doesn't have a key configured - quiet fall-back
      // to a plain text field. Other errors are transient; keep autocomplete.
      if (err?.response?.status === 503) {
        setUnavailable(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (_: any, newInputValue: string, reason: string) => {
    setInputValue(newInputValue);

    // Pass typed (or cleared) text up so an address that isn't picked from
    // the suggestions is still saved. No coordinates: the server looks the
    // address up when the record is saved.
    if (reason === 'input' || reason === 'clear') {
      onChange(newInputValue, '', '');
    }

    if (reason === 'input') {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        searchAddresses(newInputValue);
      }, 350);
    }
  };

  const handleSelect = (_: any, selected: AddressResult | string | null) => {
    if (selected && typeof selected !== 'string') {
      onChange(selected.address, String(selected.latitude), String(selected.longitude));
    }
  };

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  if (unavailable) {
    return (
      <TextField
        fullWidth
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value, '', '')}
        disabled={disabled}
        error={error}
        helperText={helperText}
        placeholder={placeholder}
      />
    );
  }

  return (
    <Autocomplete
      freeSolo
      options={options}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.address
      }
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleSelect}
      loading={loading}
      filterOptions={(x) => x}
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={error}
          helperText={helperText}
          placeholder={placeholder}
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default AzureAddressAutocomplete;
